import { API_BASE_URL, HTTP_STATUS } from "../../constants";
import api from "../../api/axios";
import { logError, logWarning, logDebug } from "./logger";
import axios from "axios";

const ENFORCE_BACKEND_VALIDATION_ACK =
    import.meta.env?.VITE_ENFORCE_BACKEND_VALIDATION_ACK === "true";

const isMutationMethod = (requestMethod) =>
    ["post", "put", "patch", "delete"].includes(
        String(requestMethod || "").toLowerCase()
    );

const hasBackendValidationAck = (data) => {
    if (!data || typeof data !== "object") return false;
    return (
        data.serverValidationPassed === true ||
        data.validationPassed === true ||
        data.backendValidationPassed === true
    );
};

const hasBackendValidationFailure = (data) => {
    if (!data || typeof data !== "object") return false;
    return (
        data.serverValidationPassed === false ||
        data.validationPassed === false ||
        data.backendValidationPassed === false
    );
};

/**
 * Generic function which calls Axios
 */
export const callAxios = async (requestMethod, url, data, headers, options = {}) => {
    try {
        const requestHeaders = { ...(headers || {}) };
        if (isMutationMethod(requestMethod)) {
            // Signal backend to apply server-side validation regardless of client checks.
            requestHeaders["x-require-server-validation"] = "true";
        }
        const response = await api({
            method: requestMethod,
            url,
            data,
            headers: requestHeaders,
            ...options,
        });

        if (response.status === HTTP_STATUS.OK && response.data) {
            if (
                isMutationMethod(requestMethod) &&
                hasBackendValidationFailure(response.data)
            ) {
                logWarning("[callAxios] Backend validation rejected request");
                return null;
            }
            if (
                ENFORCE_BACKEND_VALIDATION_ACK &&
                isMutationMethod(requestMethod) &&
                !hasBackendValidationAck(response.data)
            ) {
                logWarning("[callAxios] Missing backend validation acknowledgement");
                return null;
            }
            return response.data;
        }

        logWarning("[callAxios] Non-OK response:", response.status);
        return null;

    } catch (error) {
        logError("[callAxios] Axios error");

        if (error.response) {
            throw error; // let interceptor retry
        }

        return null;
    }
};

const is90DayRestrictionFlag = (obj) =>
    obj && (obj.is90DayRestriction === true || obj.is90DayRestriction === "true");

const tryParseJson = (value) => {
    if (typeof value !== "string") return null;
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
};

/**
 * Extracts 90-day restriction from API payloads, including when nested inside
 * badRequestResponse wrapper fields (error / errorMessage).
 */
export const parse90DayRestriction = (errorData) => {
    if (errorData == null) return null;

    let errorObj = errorData;
    if (typeof errorData === "string") {
        errorObj = tryParseJson(errorData);
        if (!errorObj) return null;
    }

    if (is90DayRestrictionFlag(errorObj)) {
        return {
            is90DayRestriction: true,
            error: errorObj.error,
        };
    }

    if (typeof errorObj === "object") {
        for (const field of [errorObj.error, errorObj.errorMessage]) {
            const nested = tryParseJson(field);
            if (is90DayRestrictionFlag(nested)) {
                return {
                    is90DayRestriction: true,
                    error: nested.error,
                };
            }
        }
    }

    return null;
};

/**
 * Handles 90-day restriction error checking
 */
const check90DayRestrictionError = (error, logTag) => {
    if (!error.response || error.response.status !== HTTP_STATUS.BAD_REQUEST) {
        return null;
    }

    const errorData = error.response.data;
    logDebug(`[${logTag}] Error data:`, errorData);

    const restriction = parse90DayRestriction(errorData);
    if (restriction) {
        logDebug(`[${logTag}] Detected 90-day restriction error`);
        return {
            error: restriction.error || "You have already applied for this position. Please try after 90 days",
            is90DayRestriction: true,
        };
    }

    return null;
};

/**
 * ✅ Common POST submit handler (USED INTERNALLY)
 */
const submitPostRequest = async ({ endpoint, payload, logTag }) => {
    const axiosHeaders = {
        "Content-Type": "application/json",
    };

    if (typeof Liferay !== "undefined" && Liferay.authToken) {
        axiosHeaders["x-csrf-token"] = Liferay.authToken;
    }

    try {
        logDebug(`[${logTag}] Submitting request`);

        const url = `${API_BASE_URL}${endpoint}`;

        const result = await callAxios(
            "post",
            url,
            payload,
            axiosHeaders
        );

        const isSuccess =
            result?.status === "OK" ||
            result?.status === "success" ||
            result?.statusCode === HTTP_STATUS.OK;

        return isSuccess ? result : null;

    } catch (error) {
        logError(`[${logTag}] Submit failed`, error);

        const restrictionError = check90DayRestrictionError(error, logTag);
        if (restrictionError) {
            return restrictionError;
        }

        return null;
    }
};

/**
 * Service class for API invocations
 */
export default class Invoke {

    static fetchFormData = async (params) => {
        const axiosHeaders = { "Content-Type": "application/json" };

        try {
            if (params.serviceName !== "restService") return null;

            const url = `${API_BASE_URL}/o/restService/${params.serviceToExecute}`;

            return await callAxios(
                params.axiosRequestMethod,
                url,
                params.requestBody,
                axiosHeaders
            );

        } catch (error) {
            logError("fetchFormData error", error);
            return null;
        }
    };

    static fetchDepartments = async () => {
        const axiosHeaders = { "Content-Type": "application/json" };

        if (typeof Liferay !== "undefined" && Liferay.authToken) {
            axiosHeaders["x-csrf-token"] = Liferay.authToken;
        }

        try {
            const url = `${API_BASE_URL}/o/inlic/webService/careerJob/departments`;
            const result = await callAxios("get", url, null, axiosHeaders);

            if (Array.isArray(result?.items)) {
                return result.items;
            }
            if (Array.isArray(result)) {
                return result;
            }
            return [];

        } catch {
            return [];
        }
    };

    static uploadDocument = async (file, applicantName) => {
        try {
            const formData = new FormData();
            formData.append("file", prepareFileForUpload(file, applicantName));

            const axiosHeaders = { Accept: "application/json" };

            if (typeof Liferay !== "undefined" && Liferay.authToken) {
                axiosHeaders["x-csrf-token"] = Liferay.authToken;
            }

            const url = `${API_BASE_URL}/o/inlic/webService/document/upload`;
            const result = await callAxios("post", url, formData, axiosHeaders);

            return result?.id
                ? { documentId: String(result.id), documentLink: result.documentLink ?? "" }
                : null;

        } catch {
            return null;
        }
    };

    static fetchBranchLocator = async () => {
        const axiosHeaders = { "Content-Type": "application/json" };

        if (typeof Liferay !== "undefined" && Liferay.authToken) {
            axiosHeaders["x-csrf-token"] = Liferay.authToken;
        }

        try {
            const url = `${API_BASE_URL}/o/inlic/webService/branchLocator`;
            return await callAxios("get", url, null, axiosHeaders);
        } catch {
            return null;
        }
    };

    static fetchAllBecomeAnAdvisor = async () => {
        const axiosHeaders = { "Content-Type": "application/json" };

        if (typeof Liferay !== "undefined" && Liferay.authToken) {
            axiosHeaders["x-csrf-token"] = Liferay.authToken;
        }

        try {
            const url = `${API_BASE_URL}/o/inlic/webService/becomeAnAdvisor`;
            return await callAxios("get", url, null, axiosHeaders);
        } catch {
            return null;
        }
    };

    /**
     * ✅ REUSED COMMON SUBMIT HANDLER
     */

    static submitCareerApplication = async (payload) => {
        const axiosHeaders = {
            "Content-Type": "application/json",
        };

        if (typeof Liferay !== "undefined" && Liferay.authToken) {
            axiosHeaders["x-csrf-token"] = Liferay.authToken;
        }

        try {
            logDebug("[submitCareerApplication] Submitting request");

            const url = `${API_BASE_URL}/o/inlic/webService/careerJob/apply`;
            const result = await callAxios("post", url, payload, axiosHeaders, {
                skipAuthRetry: true,
            });

            if (
                result?.status === "OK" ||
                result?.status === "success" ||
                result?.statusCode === HTTP_STATUS.OK
            ) {
                return result;
            }

            return null;
        } catch (error) {
            logError("[submitCareerApplication] Submit failed", error);

            const restrictionError = check90DayRestrictionError(
                error,
                "submitCareerApplication"
            );

            if (restrictionError) return restrictionError;

            return null;
        }
    };


    /**
     * Submits career application with file upload in a single multipart request.
     * This method sends both form data and resume file together.
     * 
     * @param {FormData} formData - FormData containing form fields and file
     * @returns {Promise<Object|null>} Response object or null on error
     */
    /**
     * Submits career application with file upload in a single multipart request.
     * This method sends both form data and resume file together.
     * Backend will check eligibility FIRST, then upload file and submit if eligible.
     * 
     * @param {FormData} formData - FormData containing form fields and file
     * @returns {Promise<Object|null>} Response object or null on error
     */
    static submitCareerApplicationWithFile = async (formData) => {
        // Verify it's actually FormData
        if (!(formData instanceof FormData)) {
            logError("[submitCareerApplicationWithFile] Data is not FormData instance");
            throw new Error("FormData is required");
        }

        const axiosHeaders = {
            "Accept": "application/json",
        };

        if (typeof Liferay !== "undefined" && Liferay.authToken) {
            axiosHeaders["x-csrf-token"] = Liferay.authToken;
        }

        try {
            logDebug("[submitCareerApplicationWithFile] Submitting multipart request with FormData");

            const url = `${API_BASE_URL}/o/inlic/webService/careerJob/apply`;

            const response = await api({
                method: "post",
                url,
                data: formData,
                headers: axiosHeaders,
                // Don't transform FormData - let axios handle it natively
                // This ensures Content-Type is set correctly with boundary
            });

            if (response.status === HTTP_STATUS.OK && response.data !== undefined && response.data !== null) {
                // API may return plain string "success"; normalize to object so form can check result.status === "success"
                if (response.data === "success") {
                    return { status: "success" };
                }
                return response.data;
            }

            logWarning("[submitCareerApplicationWithFile] Non-OK response:", response.status);
            return null;

        } catch (error) {
            logError("[submitCareerApplicationWithFile] Submit failed", error);

            const restrictionError = check90DayRestrictionError(error, "[submitCareerApplicationWithFile]");
            if (restrictionError) {
                return restrictionError;
            }

            throw error;
        }
    };

    static generateOTP = async (identifier) => {
        const axiosHeaders = {
            "Content-Type": "application/json",
        };

        if (typeof Liferay !== "undefined" && Liferay.authToken) {
            axiosHeaders["x-csrf-token"] = Liferay.authToken;
        }

        try {
            logDebug("[generateOTP] Generating OTP");

            const url = `${API_BASE_URL}/o/inlic/webService/generateOTP`;
            // Check if identifier is mobile number (10 digits) or email
            // API expects { email: "..." } or { mobile: "..." }
            const isMobile = /^\d{10}$/.test(identifier);
            const payload = isMobile ? { mobile: identifier } : { email: identifier };
            const result = await callAxios("post", url, payload, axiosHeaders);
            return result;
        } catch (error) {
            logError("[generateOTP] Failed", error);
            // Throw error so caller can access error.response.data
            throw error;
        }
    };


    /**
     * @param {Object} params
     * @param {string} params.otpId
     * @param {string} params.identifier
     * @param {string} params.otp
     * @param {Object} [params.verifiedSnapshot] - Optional. Locked form fields at OTP time (VAPT). Server rejects apply if request differs.
     */
    static verifyOTP = async ({ otpId, identifier, otp, verifiedSnapshot }) => {
        const axiosHeaders = {
            "Content-Type": "application/json",
        };

        if (typeof Liferay !== "undefined" && Liferay.authToken) {
            axiosHeaders["x-csrf-token"] = Liferay.authToken;
        }

        try {
            logDebug("[verifyOTP] Verifying OTP");

            const url = `${API_BASE_URL}/o/inlic/webService/verifyOTP`;
            const body = { otpId, identifier, otp };
            if (verifiedSnapshot && Object.keys(verifiedSnapshot).length > 0) {
                body.verifiedSnapshot = verifiedSnapshot;
            }
            const result = await callAxios(
                "post",
                url,
                body,
                axiosHeaders
            );

            // backend usually returns { verified: true } or similar
            return result ? result : null;

        } catch (error) {
            logError("[verifyOTP] Failed", error);
            return null;
        }
    };


    static submitBecomeAnAdvisor = async (payload) => {
        const axiosHeaders = {
            "Content-Type": "application/json",
        };

        if (typeof Liferay !== "undefined" && Liferay.authToken) {
            axiosHeaders["x-csrf-token"] = Liferay.authToken;
        }

        try {
            const url = `${API_BASE_URL}/o/inlic/webService/becomeAnAdvisor/apply`;
            const result = await callAxios("post", url, payload, axiosHeaders);

            if (
                result?.status === "OK" ||
                result?.status === "success" ||
                result?.statusCode === HTTP_STATUS.OK
            ) {
                return result;
            }

            return null;
        } catch (error) {
            logError("[submitBecomeAnAdvisor] Failed", error);
            return null;
        }
    };

    static submitGroupForm = async (payload) => {
        const axiosHeaders = {
            "Content-Type": "application/json",
        };

        if (typeof Liferay !== "undefined" && Liferay.authToken) {
            axiosHeaders["x-csrf-token"] = Liferay.authToken;
        }

        const url = `${API_BASE_URL}/o/inlic/webService/groupPolicy`;

        try {
            // If this doesn't throw → SUCCESS
            await callAxios("post", url, payload, axiosHeaders);

            return true; // ✅ explicitly success
        } catch (error) {
            logError("[submitGroupForm] Failed", error);
            throw error; 
        }
    };



    static sendBranchDetails = async (payload) =>
        submitPostRequest({
            endpoint: "/o/inlic/webService/branchLocator/send",
            payload,
            logTag: "sendBranchDetails",
        });

    static fetchPartners = async () => {
        const axiosHeaders = { "Content-Type": "application/json" };

        if (typeof Liferay !== "undefined" && Liferay.authToken) {
            axiosHeaders["x-csrf-token"] = Liferay.authToken;
        }

        try {
            const url = `${API_BASE_URL}/o/inlic/webService/contactpartners`;
            const result = await callAxios("get", url, null, axiosHeaders);

            if (Array.isArray(result?.items)) {
                return result.items;
            }
            if (Array.isArray(result)) {
                return result;
            }
            return [];

        } catch {
            return [];
        }
    };

    static fetchUncalimData = async (payload) => {
        const axiosHeaders = { "Content-Type": "application/json" };

        if (typeof Liferay !== "undefined" && Liferay.authToken) {
            axiosHeaders["x-csrf-token"] = Liferay.authToken;
        }
        try {
            const url = `${API_BASE_URL}/o/inlic/webService/getUnclaimedDetails`;
            const result = await callAxios("post", url, payload, axiosHeaders);
            return result;

        } catch {
            return null;
        }
    };

    /**
     * Fetches unclaimed senior citizen details from Upload_List (getUnclaimedSeniorCitizenDetails).
     * Payload: { dateOfBirth: "YYYY-MM-DD", lifeAssuredName, panNumber?, policyNumber? }
     * @param {Object} payload - dateOfBirth (YYYY-MM-DD), lifeAssuredName; optional: panNumber, policyNumber
     * @returns {Promise<Object|null>} { records: Array, count: number } or null on error
     */
    static fetchUnclaimedSeniorCitizenData = async (payload) => {
        const axiosHeaders = { "Content-Type": "application/json" };

        if (typeof Liferay !== "undefined" && Liferay.authToken) {
            axiosHeaders["x-csrf-token"] = Liferay.authToken;
        }
        try {
            const url = `${API_BASE_URL}/o/inlic/webService/getUnclaimedSeniorCitizenDetails`;
            const result = await callAxios("post", url, payload, axiosHeaders);
            return result;
        } catch {
            return null;
        }
    };

    static fetchSRStatus = async (payload) => {
        const axiosHeaders = { "Content-Type": "application/json" };

        if (typeof Liferay !== "undefined" && Liferay.authToken) {
            axiosHeaders["x-csrf-token"] = Liferay.authToken;
        }
        try {
            const url = `${API_BASE_URL}/o/inlic/webService/getSRStatus`;
            const result = await callAxios("post", url, payload, axiosHeaders);
            return result;

        } catch {
            return null;
        }
    };

    /**
     * Fetches latest policy info by policyNumber, clientId, or mobileNumber + dob.
     * @param {Object} params - One of: { policyNumber }, { clientId }, or { mobileNumber, dob } (dob: YYYY-MM-DD)
     * @returns {Promise<Object|null>} API response or null on error
     */
    static getLatestPolicyInfo = async (params) => {
        const axiosHeaders = { "Content-Type": "application/json" };

        if (typeof Liferay !== "undefined" && Liferay.authToken) {
            axiosHeaders["x-csrf-token"] = Liferay.authToken;
        }

        try {
            const search = new URLSearchParams();
            if (params.policyNumber) search.set("policyNumber", params.policyNumber);
            if (params.clientId) search.set("clientId", params.clientId);
            if (params.mobileNumber) search.set("mobileNumber", params.mobileNumber);
            if (params.dob) search.set("dob", params.dob);

            const query = search.toString();
            const urlPath = "/o/inlic/webService/getLatestPolicyInfo";
            const url = query ? `${API_BASE_URL}${urlPath}?${query}` : `${API_BASE_URL}${urlPath}`;
            return await callAxios("get", url, null, axiosHeaders);
        } catch (error) {      
            // Re-throw 401/403 so axios interceptor can retry with refreshed token
            if (error?.response?.status === HTTP_STATUS.UNAUTHORIZED ||
                error?.response?.status === HTTP_STATUS.FORBIDDEN) {
                throw error;
            }
            // Re-throw 503/5xx so callers can show response.data.error (e.g. "Unable to fetch policy information...")
            if (error?.response?.status >= 500) {
                throw error;
            }
            return null;
        }
    };

    static createGrievancesSR = async (payload) => {
        const axiosHeaders = { "Content-Type": "application/json" };

        if (typeof Liferay !== "undefined" && Liferay.authToken) {
            axiosHeaders["x-csrf-token"] = Liferay.authToken;
        }

        try {
            logDebug("[createGrievancesSR] Submitting request");

            const url = `${API_BASE_URL}/o/inlic/webService/createGrievancesSR`;
            const result = await callAxios("post", url, payload, axiosHeaders);

            return result;
        } catch (error) {
            logError("[createGrievancesSR] Submit failed", error);
            // Re-throw 401/403 errors to allow axios interceptor to retry with refreshed token
            if (error?.response?.status === HTTP_STATUS.UNAUTHORIZED ||
                error?.response?.status === HTTP_STATUS.FORBIDDEN) {
                throw error;
            }
            return null;
        }
    };
   static trackClaimFirst = async (payload) => {
  const axiosHeaders = { "Content-Type": "application/json" };

  if (typeof Liferay !== "undefined" && Liferay.authToken) {
    axiosHeaders["x-csrf-token"] = Liferay.authToken;
  }

  try {
    logDebug("[trackClaimFirst] Submitting request");
    
    const url = `${API_BASE_URL}/o/inlic/webService/getDeathClaimGroupStatus`;


    const result = await callAxios("post", url, payload, axiosHeaders);
    return result;
  } catch (error) {

    logError("[trackClaimFirst] Submit failed", error);

    if (
      error?.response?.status === HTTP_STATUS.UNAUTHORIZED ||
      error?.response?.status === HTTP_STATUS.FORBIDDEN
    ) {
      throw error;
    }

    return null;
  }
};

static trackClaimSecond = async (payload) => {
  const axiosHeaders = { "Content-Type": "application/json" };

  if (typeof Liferay !== "undefined" && Liferay.authToken) {
    axiosHeaders["x-csrf-token"] = Liferay.authToken;
  }

  try {
    logDebug("[trackClaimSecond] Submitting request");

      const url = `${API_BASE_URL}/o/inlic/webService/getDeathClaimIndividualStatus`;

    const result = await callAxios("post", url, payload, axiosHeaders);


    return result;
  } catch (error) {
    logError("[trackClaimSecond] Submit failed", error);

    if (
      error?.response?.status === HTTP_STATUS.UNAUTHORIZED ||
      error?.response?.status === HTTP_STATUS.FORBIDDEN
    ) {
      throw error;
    }

    return null;
  }
};


/**
     * Fetches Blogs Masters list with optional pagination.
     * All filtering, sorting, and presentation logic is handled at the frontend.
     * @param {Object} params - Optional { page, pageSize }
     * @returns {Promise<Object|null>} API response with items, totalCount, page, pageSize
     */
    static fetchBlogsMasters = async (params = {}) => {
        const axiosHeaders = { "Content-Type": "application/json" };

        if (typeof Liferay !== "undefined" && Liferay.authToken) {
            axiosHeaders["x-csrf-token"] = Liferay.authToken;
        }

        try {
            const search = new URLSearchParams();
            if (params.page) search.set("page", params.page);
            if (params.pageSize) search.set("pageSize", params.pageSize);

            const query = search.toString();
            const urlPath = "/o/inlic/webService/blogsMasters";
            const url = query ? `${API_BASE_URL}${urlPath}?${query}` : `${API_BASE_URL}${urlPath}`;
            return await callAxios("get", url, null, axiosHeaders);
        } catch (error) {
            logError("[fetchBlogsMasters] Failed", error);
            return null;
        }
    };

    /**
     * Creates a new Blogs Master record.
     * @param {Object} payload - Blog fields (blogTitle, blogCategory, blogDescription, etc.)
     * @returns {Promise<Object|null>} Created blog record or null on error
     */
    static createBlogsMaster = async (payload) =>
        submitPostRequest({
            endpoint: "/o/inlic/webService/blogsMasters",
            payload,
            logTag: "createBlogsMaster",
        });

    /**
     * Updates the view count of an existing blog record via PATCH.
     * @param {number} id - Blog record ID
     * @param {number} blogViewCounts - New view count value
     * @returns {Promise<Object|null>} Status response or null on error
     */
    static updateBlogViewCount = async (id, blogViewCounts) => {
        const axiosHeaders = { "Content-Type": "application/json" };

        if (typeof Liferay !== "undefined" && Liferay.authToken) {
            axiosHeaders["x-csrf-token"] = Liferay.authToken;
        }

        try {
            logDebug("[updateBlogViewCount] Updating view count for id=" + id);

            const url = `${API_BASE_URL}/o/inlic/webService/blogsMasters/viewCount`;
            return await callAxios("patch", url, { id, blogViewCounts }, axiosHeaders);
        } catch (error) {
            logError("[updateBlogViewCount] Failed", error);
            return null;
        }
    };

    /**
     * Fetches NAV Fund Masters list for Fund Name dropdown.
     * Pass category "Open" or "Traditional" so the REST layer applies the same filters as Liferay Object rules
     * (Open: non-Traditional with SFIN; Traditional: fundCategory Traditional). Omit category only if unfiltered list is required.
     * Returns array of fund objects (e.g. id, fullName, sFIN, fundReferenceCode). Frontend formats as "fullName (sFIN)".
     */
    static fetchNavFundMasters = async (category) => {
        const axiosHeaders = { "Content-Type": "application/json" };

        if (typeof Liferay !== "undefined" && Liferay.authToken) {
            axiosHeaders["x-csrf-token"] = Liferay.authToken;
        }

        try {
            const params = new URLSearchParams();
            params.set("_", String(Date.now()));
            const cat = category != null ? String(category).trim() : "";
            if (cat) {
                params.set("category", cat);
            }
            const url = `${API_BASE_URL}/o/inlic/webService/navFundMasters?${params.toString()}`;
            const raw = await callAxios("get", url, null, axiosHeaders);
            const result = typeof raw === "string" ? (() => { try { return JSON.parse(raw); } catch { return raw; } })() : raw;

            if (Array.isArray(result?.items)) {
                return result.items;
            }
            if (Array.isArray(result)) {
                return result;
            }
            return [];
        } catch (error) {
            logError("[fetchNavFundMasters] Failed", error);
            return [];
        }
    };

    /**
     * Fetches Our Funds Masters for Our Funds page (tabs by fundCategory + Top Funds).
     * Returns array of { id, fundCode, fundName, fundDescription, fundCategory, fundPublishDate, fundInceptionDate, fundValue, partOfTopFunds }.
     */
    static fetchOurFundsMasters = async () => {
        const axiosHeaders = { "Content-Type": "application/json" };
        if (typeof Liferay !== "undefined" && Liferay.authToken) {
            axiosHeaders["x-csrf-token"] = Liferay.authToken;
        }
        try {
            const url = `${API_BASE_URL}/o/inlic/webService/ourFundsMasters`;
            const result = await callAxios("get", url, null, axiosHeaders);
            if (Array.isArray(result?.items)) return result.items;
            if (Array.isArray(result)) return result;
            return [];
        } catch (error) {
            logError("[fetchOurFundsMasters] Failed", error);
            return [];
        }
    };

    /**
     * Fetches NAV Fund Details for a fund by fundCode (matches navFundMasters fundReferenceCode) and date range.
     * Used to build the bar chart (navDate, navValue).
     * @param {string} fundCode - Fund code (e.g. RL044)
     * @param {string} dateFrom - YYYY-MM-DD
     * @param {string} dateTo - YYYY-MM-DD
     * @returns {Promise<Array>} Array of { navDate, fundCode, id, fundName, navValue }
     */
    static fetchNavFundDetails = async (fundCode, dateFrom, dateTo) => {
        const axiosHeaders = { "Content-Type": "application/json" };

        if (typeof Liferay !== "undefined" && Liferay.authToken) {
            axiosHeaders["x-csrf-token"] = Liferay.authToken;
        }

        try {
            const params = new URLSearchParams();
            if (fundCode) params.set("fundCode", fundCode);
            if (dateFrom) params.set("dateFrom", dateFrom);
            if (dateTo) params.set("dateTo", dateTo);
            const query = params.toString();
            const queryPart = query ? `?${query}` : "";
            const url = `${API_BASE_URL}/o/inlic/webService/navFundDetails${queryPart}`;
            const raw = await callAxios("get", url, null, axiosHeaders);
            const data = raw == null ? null : (typeof raw === "string" ? (() => { try { return JSON.parse(raw); } catch { return raw; } })() : raw);

            if (Array.isArray(data)) {
                return data;
            }
            if (data && Array.isArray(data.items)) {
                return data.items;
            }
            return [];
        } catch (error) {
            logError("[fetchNavFundDetails] Failed", error);
            return [];
        }
    };

    /**
     * Fetches ECS premium offset eligibility + policy snapshot for the "ECS Premium Offset Consent" journey
     * (validates policyNumber + dob, returns policy/offset details or an eligibility error message).
     * @param {Object} params - { policyNumber, dob } dob in DD-MM-YYYY
     * @returns {Promise<Object|null>} API response ({ Status, Response, WhatsappStatus, Message }) or null on error
     */
    static getEcsPremiumOffsetDetails = async ({ policyNumber, dob }) => {
        const axiosHeaders = { "Content-Type": "application/json" };

        if (typeof Liferay !== "undefined" && Liferay.authToken) {
            axiosHeaders["x-csrf-token"] = Liferay.authToken;
        }

        try {
            const payload = {
                dateOfBirth: dob,
                policyNumber,
                premiumOffsetType: "gainPolicyValidation",
            };
            const url = `${API_BASE_URL}/o/inlic/webService/premiumOffset`;
            return await callAxios("post", url, payload, axiosHeaders);
        } catch (error) {
            logError("[getEcsPremiumOffsetDetails] Failed", error);
            // Re-throw 401/403 so axios interceptor can retry with refreshed token
            if (error?.response?.status === HTTP_STATUS.UNAUTHORIZED ||
                error?.response?.status === HTTP_STATUS.FORBIDDEN) {
                throw error;
            }
            // Re-throw 5xx so callers can show response.data.error
            if (error?.response?.status >= 500) {
                throw error;
            }
            return null;
        }
    };

    /**
     * Fetches full policy/premium figures (contract premium, guaranteed income applied, amount due,
     * existing-consent flag) for the ECS premium offset journey. Called after OTP verification, using
     * the policy's next premium/debit date as effdate.
     * @param {Object} params - { policyNumber, effdate } effdate in DD-MM-YYYY
     * @returns {Promise<Object|null>} flat API response (contractPremium, EGIAmt, amountDue, POfFlag, ...) or null on error
     */
    static getEcsPremiumOffsetPolicyInfo = async ({ policyNumber, effdate }) => {
        const axiosHeaders = { "Content-Type": "application/json" };

        if (typeof Liferay !== "undefined" && Liferay.authToken) {
            axiosHeaders["x-csrf-token"] = Liferay.authToken;
        }

        try {
            const search = new URLSearchParams();
            if (policyNumber) search.set("policy", policyNumber);
            if (effdate) search.set("effdate", effdate);
            search.set("source", "test");

            const query = search.toString();
            const urlPath = "/o/inlic/webService/premiumOffsetPolicyInfo";
            const url = query ? `${API_BASE_URL}${urlPath}?${query}` : `${API_BASE_URL}${urlPath}`;
            return await callAxios("get", url, null, axiosHeaders);
        } catch (error) {
            logError("[getEcsPremiumOffsetPolicyInfo] Failed", error);
            if (error?.response?.status === HTTP_STATUS.UNAUTHORIZED ||
                error?.response?.status === HTTP_STATUS.FORBIDDEN) {
                throw error;
            }
            if (error?.response?.status >= 500) {
                throw error;
            }
            return null;
        }
    };

    /**
     * Submits the ECS premium offset consent for a validated & OTP-verified policy.
     * Backend fills premiumOffsetTemplate.pdf and attaches it to the createGenericCase API.
     * @param {Object} payload - { policyNo, ownerName, planName, mobileNumber, amountDue, cashbackAmount, ... }
     * @returns {Promise<Object|null>} API response ({ Status, Response, Message }) or null on error
     */
    static submitEcsPremiumOffsetConsent = async (payload) => {
        const axiosHeaders = { "Content-Type": "application/json" };

        if (typeof Liferay !== "undefined" && Liferay.authToken) {
            axiosHeaders["x-csrf-token"] = Liferay.authToken;
        }

        try {
            logDebug("[submitEcsPremiumOffsetConsent] Submitting request");
            const body = {
                premiumOffsetType: "createGenericCaseWithAttchment",
                ...payload,
            };
            const url = `${API_BASE_URL}/o/inlic/webService/premiumOffset`;
            return await callAxios("post", url, body, axiosHeaders);
        } catch (error) {
            logError("[submitEcsPremiumOffsetConsent] Submit failed", error);
            if (error?.response?.status === HTTP_STATUS.UNAUTHORIZED ||
                error?.response?.status === HTTP_STATUS.FORBIDDEN) {
                throw error;
            }
            return null;
        }
    };
}

/**
 * Helper function to prepare file for upload
 */
const prepareFileForUpload = (file, applicantName) => {
    if (!applicantName) return file;

    try {
        const timestamp = Date.now();
        const ext = file.name.slice(file.name.lastIndexOf("."));
        return new File([file], `${applicantName}_${timestamp}${ext}`, {
            type: file.type,
            lastModified: file.lastModified,
        });
    } catch {
        return file;
    }
};

export const getFormDefinition = async () => {
  try {
    const siteGroupId =
      window?.Liferay?.ThemeDisplay?.getSiteGroupId();

    const response = await axios.get(
      "/o/bff-api/proxy/o/restService/getFormDefinition",
      {
        params: {
          siteGroupId,
          formName: "AI HUB Form",
        },
        headers: {
          "X-CSRF-Token": "uP99XkEP",
        },
      }
    );
    console.log("data", response.data)
    return response.data;
  } catch (error) {
    console.error("Error fetching form definition:", error);
    throw error;
  }
};

export const submitAiInnovation = async (formData) => {
  if (!(formData instanceof FormData)) {
    logError("[submitAiInnovation] Data is not FormData instance");
    throw new Error("FormData is required");
  }

  const headers = {
    Accept: "application/json",
  };

  if (typeof Liferay !== "undefined" && Liferay.authToken) {
    headers["x-csrf-token"] = Liferay.authToken;
  }

  const url = `${API_BASE_URL}/o/inlic/webService/aiInnovations`;

  const response = await api({
    method: "post",
    url,
    data: formData,
    headers,
  });

  if (response.status === HTTP_STATUS.OK && response.data !== undefined && response.data !== null) {
    return response.data;
  }

  logWarning("[submitAiInnovation] Non-OK response:", response.status);
  return null;
};
